import { hasLowerLevel, isMapId, levelAt, mapInfo, worldToRadar, type Level, type MapId, type RadarPoint } from "@69ly/maps";
import { isBotId, MAX_NOTE_PLAYERS, partAt, PF, type DemoComment, type DemoDetail, type MemberRef, type Replay } from "@69ly/shared";
import { Avatar } from "../components/Avatar.tsx";
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { focusOn, Radar } from "../components/Radar.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { HeatCanvas } from "../demo/HeatCanvas.tsx";
import { heatGrid, type HeatFilter } from "../demo/heatmap.ts";
import { matchGrenades } from "../demo/matching.ts";
import { PIN_SECONDS, ReplayCanvas, type MapPin } from "../demo/ReplayCanvas.tsx";
import { bombAt, indexRound, playersAt, roundClock, scoreboardAt, type RoundIndex } from "../demo/scene.ts";
import { weaponLabel } from "../demo/weapons.ts";
import { useDemo, useReplay } from "../lib/demo-queries.ts";
import { errorText } from "../lib/errors.ts";
import { mapIconUrl } from "../lib/map-art.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { useTeams } from "../lib/queries.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { OpenInCs2, StuffsPanel, UtilityPanel } from "./DemoPanels.tsx";
import { boardFromMoment, HeatmapPanel, NotesListPanel, NotesPanel } from "./DemoReview.tsx";
import { Popover } from "../components/Popover.tsx";
import { TIMELINE_CHOICES, useViewerSettings, type TimelineMarks, type ViewerSettings } from "../demo/viewer-settings.ts";
import { useComments, useVoices } from "../lib/demo-queries.ts";
import { useVoiceMix, useVoicePlayback } from "../demo/voice.ts";
import { VoiceSyncPanel, VoiceTracksPanel } from "./DemoVoice.tsx";
import { useMapMeshes } from "../lib/map-meshes.ts";
import { mapName } from "./Matches.tsx";
import { KillFeed, KillParts, SpeedMenu, TeamRoster } from "./DemoHud.tsx";
import { DuelsPanel, EconomyPanel } from "./DemoStats.tsx";
import { noteWhen } from "./NoteFields.tsx";

/** The 3D view and three.js load only when someone opens it. */
const Replay3D = lazy(() => import("../demo/Replay3D.tsx"));

/** Phones get the 2D view only: too small, and too slow, for 3D. */
const PHONE = "(max-width: 720px)";
const VIEW_KEY = "69ly.demoView";

function usePhone() {
  const [phone, setPhone] = useState(() => !!window.matchMedia?.(PHONE).matches);
  useEffect(() => {
    const m = window.matchMedia?.(PHONE);
    if (!m) return;
    const on = () => setPhone(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return phone;
}

/** 2D or 3D, remembered in this browser. */
function useViewChoice(): ["2d" | "3d", (v: "2d" | "3d") => void] {
  const [view, setView] = useState<"2d" | "3d">(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "3d" ? "3d" : "2d";
    } catch {
      return "2d";
    }
  });
  const choose = (v: "2d" | "3d") => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // Private window: kept for this visit only.
    }
  };
  return [view, choose];
}

export const REPLAY_SPEEDS = [0.25, 0.5, 1, 2, 4, 8] as const;
/** The analysis modes, in the header: each replaces the rosters beside the map with its own panels. */
const MODES = ["score", "events", "economy", "duels", "utility", "heatmap", "notes", "voice"] as const;
type Mode = (typeof MODES)[number];
const MODE_ICON = { score: "table", events: "list", economy: "coins", duels: "swords", utility: "grenade", heatmap: "heat", notes: "chat", voice: "mic" } as const;
type Speed = (typeof REPLAY_SPEEDS)[number];

const clock = (s: number) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};
const SIDE_CLASS = { t: "side-t", ct: "side-ct" } as const;

/**
 * Plays the replay of one round at a time: the moment in seconds since the
 * round's freeze time began, at ¼× to 8×; at the end of a round it carries on
 * into the next.
 */
function useReplayClock(replay: Replay | undefined, start: { round: number; time: number }, options: { autoNext: boolean; skipFreeze: boolean } = { autoNext: true, skipFreeze: false }) {
  const [roundIndex, setRoundIndex] = useState(start.round);
  const [time, setTime] = useState(start.time);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const ref = useRef({ time: start.time, round: start.round });
  ref.current.time = time;
  ref.current.round = roundIndex;
  const rounds = useMemo(() => replay?.rounds ?? [], [replay]);
  const lengthOf = useCallback((i: number) => (replay && rounds[i] ? (rounds[i].postTick - rounds[i].startTick) / replay.tickRate : 0), [replay, rounds]);
  const length = lengthOf(roundIndex);
  const opts = useRef(options);
  opts.current = options;
  // Where a round starts playing: at its freeze time, or a second before it goes live.
  const startOf = useCallback(
    (i: number) => {
      const r = rounds[i];
      if (!replay || !r || !opts.current.skipFreeze) return 0;
      return Math.max(0, (r.liveTick - r.startTick) / replay.tickRate - 1);
    },
    [replay, rounds],
  );

  useEffect(() => {
    if (!playing || !replay) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      let next = ref.current.time + dt * speed;
      if (next >= lengthOf(ref.current.round)) {
        if (opts.current.autoNext && ref.current.round + 1 < rounds.length) {
          ref.current.round += 1;
          next = startOf(ref.current.round);
          setRoundIndex(ref.current.round);
        } else {
          next = lengthOf(ref.current.round);
          setTime(next);
          setPlaying(false);
          return;
        }
      }
      ref.current.time = next;
      setTime(next);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, speed, replay, rounds.length, lengthOf, startOf]);

  const seek = useCallback((s: number) => setTime(Math.max(0, Math.min(lengthOf(ref.current.round), s))), [lengthOf]);
  const goRound = useCallback(
    (i: number, at?: number) => {
      if (i < 0 || i >= rounds.length) return;
      ref.current.round = i;
      setRoundIndex(i);
      setTime(Math.max(0, Math.min(lengthOf(i), at ?? startOf(i))));
    },
    [rounds.length, lengthOf, startOf],
  );
  return {
    roundIndex,
    time,
    length,
    playing,
    speed,
    setSpeed,
    seek,
    goRound,
    play: () => setPlaying(true),
    pause: () => setPlaying(false),
    toggle: () => setPlaying((p) => !p),
  };
}

/** The demo viewer: a round on the map, the scoreboard and the round's events beside it, the timeline under. */
export function DemoViewerPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const demo = useDemo(id);
  const location = useLocation();
  // A relaunched map's later demo opens the whole map, under its first demo's address.
  const moved = !!demo.data && demo.data.id !== id;
  const replay = useReplay(moved ? undefined : demo.data);
  if (moved) return <Navigate to={`/demos/${demo.data!.id}${location.search}`} replace />;
  if (demo.isPending || (demo.data && replay.isPending))
    return (
      <div className="page">
        <Loading />
        {demo.data && (
          <p className="muted small center">
            {t("demos.viewer.loading", {
              mb: (demo.data.replayBytes / 1e6).toFixed(1),
            })}
          </p>
        )}
      </div>
    );
  if (demo.error) return <ErrorState message={errorText(t, demo.error)} onRetry={() => void demo.refetch()} />;
  if (replay.error || !replay.data) return <ErrorState message={replay.error ? t("demos.viewer.unreadable") : t("common.error")} onRetry={() => void replay.refetch()} />;
  if (!isMapId(replay.data.map)) return <ErrorState message={t("demos.viewer.unknownMap", { map: replay.data.map })} />;
  return <Viewer key={demo.data.id} demo={demo.data} replay={replay.data} map={replay.data.map} />;
}

/** How far the 2D map may be moved past its edges: out from under the side panels and the timeline. */
const MAP_OVERSCROLL = { x: 260, y: 140 };
/** How near a click must be to a player (a share of the radar) to add them to a note. */
const PICK_RADIUS = 0.025;

function Viewer({ demo, replay, map }: { demo: DemoDetail; replay: Replay; map: MapId }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const startRound = Math.max(0, Math.min(replay.rounds.length - 1, Number(params.get("r") ?? 1) - 1 || 0));
  // A moment given as a tick (notes listed on the match page) or as seconds into the round.
  const startTick = Number(params.get("tk"));
  const [settings, setSettings] = useViewerSettings();
  const openRound = replay.rounds[startRound];
  // No moment asked: the round opens where any other would, after its freeze time when it is skipped.
  const startTime =
    Number.isFinite(startTick) && startTick > 0 && openRound
      ? Math.max(0, (startTick - openRound.startTick) / replay.tickRate - 1)
      : params.has("t")
        ? Math.max(0, Number(params.get("t")) || 0)
        : settings.skipFreeze && openRound
          ? Math.max(0, (openRound.liveTick - openRound.startTick) / replay.tickRate - 1)
          : 0;
  const play = useReplayClock(replay, { round: startRound, time: startTime }, settings);
  const round = useMemo<RoundIndex>(() => indexRound(replay, play.roundIndex), [replay, play.roundIndex]);
  const tick = round.round.startTick + play.time * replay.tickRate;
  const [followed, setFollowed] = useState<number | null>(null);
  // An analysis mode replaces the rosters beside the map; the map keeps its room.
  const [mode, setModeNow] = useState<Mode | null>(() => {
    const m = params.get("m");
    return (MODES as readonly string[]).includes(m ?? "") ? (m as Mode) : null;
  });
  const setMode = (m: Mode | null) => {
    setModeNow(m);
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (m) n.set("m", m);
        else n.delete("m");
        return n;
      },
      { replace: true },
    );
  };
  // The players a new note is about: toggled in its form, or by a click on them on the map.
  const [notePlayers, setNotePlayers] = useState<number[]>([]);
  const theater = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // The page fills the window under the app's header, whatever its height.
    const header = document.querySelector(".app-header");
    const el = theater.current;
    if (!header || !el) return;
    const fit = () => el.style.setProperty("--app-header-h", `${Math.round(header.getBoundingClientRect().height)}px`);
    fit();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(fit);
    ro?.observe(header);
    return () => ro?.disconnect();
  }, []);
  const [heat, setHeat] = useState<HeatFilter>({
    what: "presence",
    rounds: "all",
    side: "both",
    player: null,
  });
  // Grenades are matched against the match's team library; a pro match's against the viewer's own.
  const { scope: myScope } = useCurrentTeam();
  const libraryScope = demo.match.teamId ?? myScope;
  const teams = useTeams().data ?? [];
  const libraryName = libraryScope === "personal" ? t("demos.stuffs.personal") : (teams.find((x) => x.id === libraryScope)?.name ?? "");
  const stuffList = useStuffs(libraryScope, map);
  const grenadeMatches = useMemo(() => matchGrenades(replay, stuffList.data ?? [], new Set(demo.dismissed.map(([g, s]) => `${g}:${s}`))), [replay, stuffList.data, demo.dismissed]);
  const labels = useMemo(() => new Map(grenadeMatches.filter((m) => m.stuff).map((m) => [m.grenade, m.stuff!.title])), [grenadeMatches]);
  const comments = useComments(demo.id).data ?? [];
  // Voice comms played along: they go on whatever panel is open.
  const voiceList = useVoices(demo.id).data;
  const voices = useMemo(() => voiceList ?? [], [voiceList]);
  const [mix, setMix] = useVoiceMix(demo.id);
  const [voicePick, setVoicePick] = useState<string | null>(null);
  const voiceTrack = voices.find((v) => v.id === voicePick) ?? voices[0] ?? null;
  const hasLevels = hasLowerLevel(map);
  const [level, setLevel] = useState<Level>("upper");
  const phone = usePhone();
  // The dock over the map fades after 2.5 s without a move while it plays (not under the pointer, nor with the focus in it).
  const [dockHover, setDockHover] = useState(false);
  const [dockFocus, setDockFocus] = useState(false);
  const [awake, setAwake] = useState(0);
  const [dockRest, setDockRest] = useState(false);
  const wake = useCallback(() => setAwake((n) => n + 1), []);
  useEffect(() => {
    setDockRest(false);
    const id = window.setTimeout(() => setDockRest(true), 2500);
    return () => window.clearTimeout(id);
  }, [awake]);
  const meshInfo = useMapMeshes().data?.find((m) => m.map === map);
  const [viewChoice, setViewChoice] = useViewChoice();
  const can3d = !!meshInfo && !phone;

  // The radar opens on where the game was played: the empty edges of Valve's image cut away.
  const fit = useMemo(() => {
    const { x, y, flags } = replay.columns;
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (let i = 0; i < flags.length; i += 7) {
      if (!(flags[i] & PF.alive)) continue;
      x0 = Math.min(x0, x[i]);
      x1 = Math.max(x1, x[i]);
      y0 = Math.min(y0, y[i]);
      y1 = Math.max(y1, y[i]);
    }
    if (!Number.isFinite(x0)) return undefined;
    return focusOn(
      map,
      [
        { x: x0, y: y0 },
        { x: x1, y: y1 },
      ],
      { pad: 0.05, maxScale: 1.6 },
    );
  }, [replay, map]);

  // Each player's number within their team, 1 to 5, in the order they appear.
  const numbers = useMemo(() => {
    const seen = [0, 0];
    return replay.players.map((p) => ++seen[p.team]);
  }, [replay]);

  // The address follows the round and, when paused, the moment: a link opens there.
  useEffect(() => {
    if (play.playing) return;
    const r = String(play.roundIndex + 1);
    const tt = play.time.toFixed(1);
    if (params.get("r") === r && params.get("t") === tt) return;
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set("r", r);
        n.set("t", tt);
        return n;
      },
      { replace: true },
    );
  }, [play.playing, play.roundIndex, play.time, params, setParams]);

  // Keys: Space, ← → (2 s), Shift+← → or [ ] (rounds), , . (a frame), Home.
  const playRef = useRef(play);
  playRef.current = play;
  const setMixRef = useRef(setMix);
  setMixRef.current = setMix;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement;
      const onSeek = target instanceof HTMLInputElement && target.type === "range";
      if (target.closest("input, textarea, select, [contenteditable]") && !onSeek) return;
      const p = playRef.current;
      const step = 1 / replay.sampleRate;
      const actions: Record<string, () => void> = {
        " ": p.toggle,
        ArrowLeft: () => (e.shiftKey ? p.goRound(p.roundIndex - 1) : p.seek(p.time - 2)),
        ArrowRight: () => (e.shiftKey ? p.goRound(p.roundIndex + 1) : p.seek(p.time + 2)),
        ",": () => (p.pause(), p.seek(p.time - step)),
        ".": () => (p.pause(), p.seek(p.time + step)),
        "[": () => p.goRound(p.roundIndex - 1),
        "]": () => p.goRound(p.roundIndex + 1),
        Home: () => p.seek(0),
        Escape: () => setModeNow(null),
        v: () => setMixRef.current((m) => ({ on: !m.on })),
      };
      const run = actions[e.key];
      if (!run) return;
      if (e.key === " " && target.closest("button")) return;
      // The seek bar moves itself with the arrows and Home.
      if (onSeek && !e.shiftKey && (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Home")) return;
      e.preventDefault();
      run();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [replay.sampleRate]);

  const heatmap = useMemo(() => (mode === "heatmap" ? heatGrid(replay, map, heat, play.roundIndex, hasLevels ? level : null) : null), [mode, replay, map, heat, play.roundIndex, hasLevels, level]);
  // The heatmap is drawn on the radar: the 3D view waits while it is open.
  const show3d = can3d && viewChoice === "3d" && !heatmap;
  const voice = useVoicePlayback({ demoId: demo.id, tracks: voices, mix, replay, tick, playing: play.playing, speed: play.speed });
  const players = playersAt(replay, round, tick);
  const followedView = followed !== null ? players.find((p) => p.index === followed) : undefined;
  // Following a player on a two-level map: their level.
  const followedLevel = hasLevels && followedView?.alive ? levelAt(map, followedView.z) : null;
  useEffect(() => {
    if (followedLevel) setLevel(followedLevel);
  }, [followedLevel]);

  const roundNow = round.round;
  const scoreBefore = play.roundIndex > 0 ? replay.rounds[play.roundIndex - 1].score : ([0, 0] as [number, number]);
  const decided = tick >= roundNow.endTick;
  const score = decided ? roundNow.score : scoreBefore;
  const tTeam = roundNow.tTeam;
  const sideOfTeam = (team: 0 | 1) => (team === tTeam ? "t" : "ct");
  const c = roundClock(replay, round, tick);
  const bomb = bombAt(replay, round, tick);
  const match = demo.match;
  // Side A of the match first, named as the match names it (our team when we played).
  const first = demo.aTeam;
  const nameOf = (team: 0 | 1) => (team === first ? match.sides[0] : match.sides[1]);
  const bySteam = new Map(demo.players.map((p) => [p.steamId, p.member]));
  const memberOf = (i: number) => bySteam.get(replay.players[i]?.steamId ?? "") ?? null;
  const nextMap = match.maps.find((m) => m.number === demo.number + 1) ?? null;
  const atEnd = play.roundIndex === replay.rounds.length - 1 && play.time >= play.length - 0.05;
  const views = new Map(players.map((p) => [p.index, p]));
  // With the Notes mode open, a click on a player on the 2D map adds them to the new note (or takes them off).
  const picking = mode === "notes" && !show3d;
  // The timeline's notes, as the settings say: every note, the viewer's own, or none.
  const shownNote = (c: DemoComment) => settings.timeline.notes === "all" || (settings.timeline.notes === "mine" && c.canEdit);
  // "Ours" on the timeline: side A of the match (our team when we played it).
  const ours = first;
  const pickOnMap = (p: RadarPoint) => {
    let best: { index: number; d: number } | null = null;
    for (const v of players) {
      if (!v.alive || (hasLevels && levelAt(map, v.z) !== level)) continue;
      const at = worldToRadar(map, v.x, v.y);
      const d = Math.hypot(at.u - p.u, at.v - p.v);
      if (d < PICK_RADIUS && (!best || d < best.d)) best = { index: v.index, d };
    }
    if (!best) return;
    const i = best.index;
    setNotePlayers((ps) => (ps.includes(i) ? ps.filter((x) => x !== i) : ps.length >= MAX_NOTE_PLAYERS ? ps : [...ps, i]));
  };
  // Notes of this round show on the map from their moment, for a few seconds or as long as
  // they last: their spot, and a ring on each of their players; the players of the new note too.
  const pins: MapPin[] = [
    ...comments
      .filter((c) => c.round === play.roundIndex && c.tick <= tick && tick <= (c.endTick ?? c.tick + PIN_SECONDS * replay.tickRate))
      .flatMap((c): MapPin[] => [
        ...(c.pin ? [{ pin: c.pin, text: c.text, draft: false }] : []),
        ...c.players.map((p, k) => ({ pin: { kind: "player" as const, player: p.index }, text: !c.pin && k === 0 ? c.text : "", draft: false })),
      ]),
    ...(mode === "notes" ? notePlayers.map((i) => ({ pin: { kind: "player" as const, player: i }, text: "", draft: true })) : []),
  ];
  const rows = scoreboardAt(replay, tick);
  const ctTeam = (1 - tTeam) as 0 | 1;
  const roster = (team: 0 | 1) => (
    <TeamRoster
      replay={replay}
      team={team}
      side={sideOfTeam(team)}
      name={nameOf(team)}
      views={views}
      rows={rows}
      tick={tick}
      numbers={numbers}
      memberOf={memberOf}
      followed={followed}
      onFollow={(i) => setFollowed(followed === i ? null : i)}
    />
  );

  // Auto zoom: the map follows the players still alive, in steps (so it glides now and then, not every frame).
  const alive = players.filter((p) => p.alive);
  const zoomKey =
    settings.autoZoom && alive.length
      ? (() => {
          const f = focusOn(
            map,
            alive.map((p) => ({ x: p.x, y: p.y })),
            { pad: 0.09, maxScale: 2.4 },
          );
          if (!f) return "whole";
          const q = (n: number, step: number) => Math.round(n / step) * step;
          return `${q(f.u, 0.04).toFixed(2)} ${q(f.v, 0.04).toFixed(2)} ${q(f.scale, 0.25).toFixed(2)}`;
        })()
      : null;
  const autoFocus = useMemo(() => {
    if (!zoomKey || zoomKey === "whole") return undefined;
    const [u, v, scale] = zoomKey.split(" ").map(Number);
    return { u, v, scale };
  }, [zoomKey]);
  const radarFocus = settings.autoZoom ? autoFocus : fit;

  const stacked = (
    <div className="hud-stack">
      {roster(tTeam)}
      {roster(ctTeam)}
    </div>
  );
  /** Each mode's two panels: the left one, and the right one (the rosters when the mode has only one). */
  const panels = (m: Mode): { left: ReactNode; right: ReactNode | null } => {
    switch (m) {
      case "score":
        return {
          left: (
            <Scoreboard
              only={tTeam}
              replay={replay}
              tick={tick}
              nameOf={nameOf}
              memberOf={memberOf}
              first={first}
              players={players}
              tTeam={tTeam}
              followed={followed}
              onFollow={(i) => setFollowed(followed === i ? null : i)}
              numbers={numbers}
            />
          ),
          right: (
            <Scoreboard
              only={ctTeam}
              replay={replay}
              tick={tick}
              nameOf={nameOf}
              memberOf={memberOf}
              first={first}
              players={players}
              tTeam={tTeam}
              followed={followed}
              onFollow={(i) => setFollowed(followed === i ? null : i)}
              numbers={numbers}
            />
          ),
        };
      case "events":
        return { left: <RoundEvents replay={replay} round={round} tick={tick} onSeek={(sec) => play.seek(sec)} numbers={numbers} />, right: stacked };
      case "economy":
        return {
          left: <EconomyPanel part="map" replay={replay} round={round} numbers={numbers} first={first} nameOf={nameOf} onGo={(i, sec) => play.goRound(i, sec)} />,
          right: <EconomyPanel part="round" replay={replay} round={round} numbers={numbers} first={first} nameOf={nameOf} onGo={(i, sec) => play.goRound(i, sec)} />,
        };
      case "duels":
        return {
          left: <DuelsPanel part="players" replay={replay} round={round} numbers={numbers} first={first} nameOf={nameOf} onGo={(i, sec) => play.goRound(i, sec)} />,
          right: <DuelsPanel part="list" replay={replay} round={round} numbers={numbers} first={first} nameOf={nameOf} onGo={(i, sec) => play.goRound(i, sec)} />,
        };
      case "utility":
        return {
          left: <UtilityPanel replay={replay} round={round} matches={grenadeMatches} numbers={numbers} onSeek={(sec) => play.seek(sec)} />,
          right: (
            <StuffsPanel
              demoId={demo.id}
              replay={replay}
              map={map}
              round={round}
              matches={grenadeMatches}
              stuffs={stuffList.data ?? null}
              dismissed={demo.dismissed}
              libraryName={libraryName}
              numbers={numbers}
              note={(n, thrower) =>
                t("demos.stuffs.note", {
                  match: match.title.includes(mapInfo(map).name) ? match.title : `${match.title}, ${mapInfo(map).name}`,
                  round: n,
                  thrower,
                })
              }
              onSeek={(sec) => play.seek(sec)}
            />
          ),
        };
      case "voice":
        return {
          left: <VoiceTracksPanel demo={demo} tracks={voices} mix={mix} onMix={setMix} playback={voice} selected={voiceTrack?.id ?? null} onSelect={setVoicePick} speed={play.speed} />,
          right: <VoiceSyncPanel demo={demo} replay={replay} round={round} tick={tick} track={voiceTrack} playback={voice} />,
        };
      case "heatmap":
        return { left: <HeatmapPanel replay={replay} round={round} filter={heat} onChange={setHeat} samples={heatmap?.samples ?? 0} />, right: null };
      case "notes":
        return {
          left: (
            <NotesPanel
              players={notePlayers}
              onPlayers={setNotePlayers}
              numbers={numbers}
              teamNames={[nameOf(0), nameOf(1)]}
              demo={demo}
              replay={replay}
              round={round}
              tick={tick}
              time={play.time}
              makeBoard={() =>
                boardFromMoment({
                  replay,
                  map,
                  round,
                  tick,
                  numbers,
                  matches: grenadeMatches,
                  side: match.outcome !== null ? (first === round.round.tTeam ? "t" : "ct") : "t",
                  title: `${match.title} · R${round.round.number} · ${clock(play.time)}`,
                  phase: `R${round.round.number} · ${clock(play.time)}`,
                  notes: t("demos.notes.boardNote", {
                    match: match.title.includes(mapInfo(map).name) ? match.title : `${match.title}, ${mapInfo(map).name}`,
                    n: round.round.number,
                    at: clock(play.time),
                    clock: clock(c.seconds),
                    url: `${window.location.origin}/demos/${demo.id}?r=${round.round.number}&t=${play.time.toFixed(1)}`,
                  }),
                  scope: libraryScope,
                })
              }
            />
          ),
          right: <NotesListPanel demo={demo} replay={replay} round={round} time={play.time} numbers={numbers} teamNames={[nameOf(0), nameOf(1)]} onGo={(i, sec) => play.goRound(i, sec)} />,
        };
    }
  };
  const side = mode ? panels(mode) : { left: roster(tTeam), right: roster(ctTeam) };
  const panel = (content: ReactNode, where: "left" | "right") =>
    mode && content !== stacked ? (
      <section className={`theater-panel is-${where}`} aria-label={t(`demos.modes.${mode}`)}>
        {where === "left" && (
          <header className="theater-panel-head">
            <Icon name={MODE_ICON[mode]} />
            <h2>{t(`demos.modes.${mode}`)}</h2>
            <button type="button" className="btn btn-quiet btn-small theater-panel-close" aria-label={t("demos.modes.close")} title={t("demos.modes.close")} onClick={() => setMode(null)}>
              <Icon name="close" />
            </button>
          </header>
        )}
        <div className="theater-panel-body">{content}</div>
      </section>
    ) : (
      content
    );
  const cs2 = partAt(replay, tick);

  // The rounds and the transport: over the bottom of the map (the map keeps the whole height),
  // under it on a phone. They fade while it plays and the pointer rests elsewhere.
  const dockIdle = !phone && play.playing && dockRest && !dockHover && !dockFocus;
  const dock = (
    <div
      className="theater-dock"
      onPointerEnter={() => setDockHover(true)}
      onPointerLeave={() => setDockHover(false)}
      onFocus={() => setDockFocus(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDockFocus(false);
      }}
    >
      <RoundStrip replay={replay} current={play.roundIndex} onPick={(i) => play.goRound(i)} />
      <Transport replay={replay} round={round} play={play} notes={comments.filter((x) => x.round === play.roundIndex && shownNote(x))} timeline={settings.timeline} ours={ours}>
        <Popover icon="gear" label={t("demos.settings.title")} className="theater-settings" above>
          <ViewerSettingsPanel settings={settings} onChange={setSettings} />
        </Popover>
      </Transport>
    </div>
  );

  return (
    <div
      ref={theater}
      className={`theater${mode ? ` has-mode is-mode-${mode}` : ""}${show3d ? " is-3d" : ""}${dockIdle ? " is-dock-idle" : ""}`}
      onPointerMove={wake}
      onPointerDown={wake}
      onKeyDown={wake}
    >
      <header className="theater-top">
        <Link to={`/matches/${match.id}`} className="btn btn-quiet btn-small theater-back" aria-label={t("demos.viewer.backToMatch", { title: match.title })}>
          <Icon name="chevronLeft" size={16} />
          <span className="theater-back-label">{t("demos.viewer.match")}</span>
        </Link>
        <div className="demo-title">
          <img src={mapIconUrl(map)} alt="" width={28} height={28} />
          <div>
            <h1>{match.title}</h1>
            <p className="muted small">
              {[mapInfo(map).name, match.maps.length > 1 || match.bestOf > 1 ? t("matches.mapOf", { n: demo.number, bo: match.bestOf }) : null, match.event].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        {match.maps.length > 1 && (
          <nav className="demo-match-bar" aria-label={t("matches.maps")}>
            {match.maps.map((m) => (
              <Link key={m.demoId} to={`/demos/${m.demoId}`} className="demo-match-map" aria-current={m.demoId === demo.id ? "page" : undefined}>
                <span className="mono">{m.number}</span>
                <span>{mapName(m.map)}</span>
                <b className="mono">
                  {m.score[0]}–{m.score[1]}
                </b>
                {match.outcome !== null && m.winner && <span className="visually-hidden">{t(m.winner === "a" ? "matches.outcome.win" : "matches.outcome.loss")}</span>}
              </Link>
            ))}
          </nav>
        )}
        <div
          className="demo-scoreline"
          aria-label={t("demos.viewer.scoreAria", {
            a: nameOf(tTeam),
            b: nameOf(ctTeam),
            sa: score[tTeam],
            sb: score[ctTeam],
          })}
        >
          <span className={`demo-team ${SIDE_CLASS.t}`}>{nameOf(tTeam)}</span>
          <b className="mono">
            <span className="side-t">{score[tTeam]}</span> : <span className="side-ct">{score[ctTeam]}</span>
          </b>
          <span className={`demo-team ${SIDE_CLASS.ct}`}>{nameOf(ctTeam)}</span>
        </div>
        <div className={`demo-clock is-${c.phase}`} role="timer" aria-live="off">
          <span className="eyebrow">
            {t("demos.viewer.round", {
              n: roundNow.number,
              of: replay.rounds.length,
            })}
          </span>
          <b className="mono">{clock(c.seconds)}</b>
          <span className="small">{bomb.state === "planted" && bomb.left !== null ? t("demos.viewer.bombLeft", { s: Math.ceil(bomb.left) }) : t(`demos.viewer.phase.${c.phase}`)}</span>
        </div>
      </header>

      <div className="theater-bar">
        <div className="theater-modes" role="toolbar" aria-label={t("demos.modes.label")}>
          {MODES.map((m) => (
            <button key={m} type="button" className="mode-button" aria-pressed={mode === m} title={t(`demos.modes.${m}Help`)} onClick={() => setMode(mode === m ? null : m)}>
              <Icon name={MODE_ICON[m]} />
              <span>{t(`demos.modes.${m}`)}</span>
            </button>
          ))}
        </div>
        <div className="theater-actions">
          {can3d && (
            <div className="segmented segmented-small" role="group" aria-label={t("demos.three.view")}>
              {(["2d", "3d"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className="seg"
                  aria-pressed={(v === "3d") === show3d}
                  disabled={v === "3d" && !!heatmap}
                  title={v === "3d" && heatmap ? t("demos.three.heatIn2d") : undefined}
                  onClick={() => setViewChoice(v)}
                >
                  {t(`demos.three.${v}`)}
                </button>
              ))}
            </div>
          )}
          {hasLevels && !show3d && (
            <div className="segmented segmented-small" role="group" aria-label={t("level.label")}>
              {(["upper", "lower"] as const).map((l) => (
                <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                  {t(`level.${l}`)}
                </button>
              ))}
            </div>
          )}
          {voices.length > 0 && (
            <button
              type="button"
              className="btn btn-quiet btn-small btn-icon theater-voice"
              aria-pressed={mix.on}
              aria-label={t("demos.voice.toggle")}
              title={`${t(mix.on ? "demos.voice.turnOff" : "demos.voice.turnOn")} (V)`}
              onClick={() => setMix((m) => ({ on: !m.on }))}
            >
              <Icon name={mix.on ? "volume" : "muted"} />
            </button>
          )}
          {match.canEdit && (
            <Link
              to={`/demos/import?to=${match.pro ? "pro" : match.teamId}&again=${demo.id}`}
              className="btn btn-quiet btn-small btn-icon"
              aria-label={t("demos.import.again")}
              title={`${t("demos.import.again")}: ${t("demos.import.againHelp")}`}
            >
              <Icon name="loop" />
            </Link>
          )}
          <Popover icon="terminal" label={t("demos.cs2.title")}>
            <OpenInCs2 inline fileName={demo.parts?.[cs2.part]?.fileName ?? demo.fileName} tick={cs2.tick} player={followed !== null ? (replay.players[followed]?.name ?? null) : null} />
          </Popover>
          <Popover icon="help" label={t("demos.help.title")}>
            <KeysHelp />
          </Popover>
        </div>
      </div>

      <div className="theater-stage">
        <div className="theater-map">
          {show3d && meshInfo ? (
            <Suspense fallback={<Loading />}>
              <Replay3D map={map} meshInfo={meshInfo} replay={replay} round={round} tick={tick} followed={followed} onFollow={(i) => setFollowed(i)} numbers={numbers} labels={labels} />
            </Suspense>
          ) : (
            <Radar
              turnable
              bleedInto=".theater-stage"
              overscroll={phone ? undefined : MAP_OVERSCROLL}
              map={map}
              mapName={mapInfo(map).name}
              level={level}
              focus={radarFocus}
              onPick={picking ? pickOnMap : undefined}
            >
              {heatmap ? (
                <HeatCanvas grid={heatmap.grid} />
              ) : (
                <ReplayCanvas
                  map={map}
                  replay={replay}
                  round={round}
                  tick={tick}
                  level={level}
                  hasLevels={hasLevels}
                  followed={followed}
                  numbers={numbers}
                  labels={labels}
                  pins={pins}
                  names={settings.names}
                />
              )}
            </Radar>
          )}
        </div>

        {!phone && (
          <>
            <div className={`hud-side is-left${mode ? " is-panel" : ""}`}>{panel(side.left, "left")}</div>
            {side.right && <div className={`hud-side is-right${mode ? " is-panel" : ""}`}>{panel(side.right, "right")}</div>}
          </>
        )}
        <KillFeed replay={replay} round={round} tick={tick} numbers={numbers} followed={followed} />
        {!phone && dock}
      </div>

      {phone && dock}

      {phone && (
        <div className="theater-rosters">
          {panel(side.left, "left")}
          {side.right && panel(side.right, "right")}
        </div>
      )}

      {nextMap && atEnd && (
        <div className="card demo-next" role="status">
          <span>{t("demos.viewer.mapOver", { map: mapInfo(map).name })}</span>
          <Link to={`/demos/${nextMap.demoId}`} className="btn btn-primary">
            {t("demos.viewer.nextMap", {
              n: nextMap.number,
              map: mapName(nextMap.map),
            })}
            <Icon name="chevronRight" />
          </Link>
        </div>
      )}
    </div>
  );
}

/** The viewer's settings: how it plays, kept in this browser. */
function ViewerSettingsPanel({ settings, onChange }: { settings: ViewerSettings; onChange: (c: Partial<ViewerSettings>) => void }) {
  const { t } = useTranslation();
  const keys = ["autoNext", "skipFreeze", "autoZoom", "names"] as const;
  return (
    <div className="viewer-settings">
      <h2 className="eyebrow">{t("demos.settings.playback")}</h2>
      {keys.map((k) => (
        <label key={k} className="switch-row">
          <span>
            <b>{t(`demos.settings.${k}`)}</b>
            <span className="muted small">{t(`demos.settings.${k}Help`)}</span>
          </span>
          <input type="checkbox" role="switch" className="switch" checked={settings[k]} onChange={(e) => onChange({ [k]: e.target.checked })} />
        </label>
      ))}
      <div className="viewer-settings-section">
        <h2 className="eyebrow">{t("demos.settings.timeline")}</h2>
        {(Object.keys(TIMELINE_CHOICES) as (keyof TimelineMarks)[]).map((k) => (
          <fieldset key={k} className="form-field timeline-row">
            <legend>
              <span className={`timeline-swatch is-${k}`} aria-hidden="true" />
              <b>{t(`demos.settings.marks.${k}`)}</b>
            </legend>
            <span className="muted small">{t(`demos.settings.marks.${k}Help`)}</span>
            <div className="segmented segmented-small">
              {TIMELINE_CHOICES[k].map((v) => (
                <button key={v} type="button" className="seg" aria-pressed={settings.timeline[k] === v} onClick={() => onChange({ timeline: { ...settings.timeline, [k]: v } })}>
                  {t(k === "kills" && v === "ours" ? "demos.settings.choice.ourPlayers" : `demos.settings.choice.${v}`)}
                </button>
              ))}
            </div>
          </fieldset>
        ))}
        <p className="muted small">{t("demos.settings.kept")}</p>
      </div>
    </div>
  );
}

/** What the keyboard and the mouse do in the viewer. */
function KeysHelp() {
  const { t } = useTranslation();
  const rows: [string, string][] = [
    ["Space", t("demos.help.play")],
    ["← →", t("demos.help.seek")],
    ["Shift ← →  [ ]", t("demos.help.rounds")],
    [", .", t("demos.help.frame")],
    ["Home", t("demos.help.start")],
    ["Esc", t("demos.help.closeMode")],
    ["V", t("demos.help.voice")],
  ];
  return (
    <div className="keys-help">
      <h2 className="eyebrow">{t("demos.help.keys")}</h2>
      <dl>
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>
              {k.split("  ").map((part) => (
                <kbd key={part}>{part}</kbd>
              ))}
            </dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <h2 className="eyebrow">{t("demos.help.mouse")}</h2>
      <ul>
        <li>{t("demos.help.follow")}</li>
        <li>{t("demos.help.zoom")}</li>
        <li>{t("demos.help.modes")}</li>
      </ul>
    </div>
  );
}

function Scoreboard({
  only,
  replay,
  tick,
  nameOf,
  memberOf,
  first,
  players,
  tTeam,
  followed,
  onFollow,
  numbers,
}: {
  /** One team only (a panel each); both otherwise. */
  only?: 0 | 1;
  replay: Replay;
  tick: number;
  nameOf: (team: 0 | 1) => string;
  memberOf: (i: number) => MemberRef | null;
  first: 0 | 1;
  players: ReturnType<typeof playersAt>;
  tTeam: 0 | 1;
  followed: number | null;
  onFollow: (i: number) => void;
  numbers: number[];
}) {
  const { t } = useTranslation();
  const rows = scoreboardAt(replay, tick);
  const now = new Map(players.map((p) => [p.index, p]));
  return (
    <div className="demo-scoreboard">
      {(only !== undefined ? [only] : ([first, (1 - first) as 0 | 1] as const)).map((team) => {
        const side = team === tTeam ? "t" : "ct";
        // A bot standing in for a player who left is listed only while it plays.
        const members = replay.players.map((p, i) => ({ p, i })).filter((x) => x.p.team === team && (!isBotId(x.p.steamId) || now.get(x.i)?.alive));
        return (
          <table key={team} className={`demo-score-table ${SIDE_CLASS[side]}`}>
            <caption>
              <span className={`live-side ${SIDE_CLASS[side]}`}>{t(`live.sideShort.${side}`)}</span> {nameOf(team)}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t("demos.viewer.cols.player")}</th>
                <th scope="col" className="num">
                  {t("demos.viewer.cols.money")}
                </th>
                <th scope="col" className="num" title={t("demos.viewer.cols.kdaHelp")}>
                  {t("demos.viewer.cols.kda")}
                </th>
                <th scope="col" className="num">
                  {t("demos.viewer.cols.adr")}
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map(({ p, i }) => {
                const v = now.get(i);
                const r = rows[i];
                const alive = v?.alive ?? false;
                return (
                  <tr key={i} className={`${alive ? "" : "is-dead"}${followed === i ? " is-followed" : ""}`}>
                    <th scope="row">
                      <div className="demo-player-row">
                        <button type="button" className="demo-player" aria-pressed={followed === i} onClick={() => onFollow(i)} title={t("demos.viewer.follow", { name: p.name })}>
                          <span className={`demo-num ${SIDE_CLASS[side]}`}>{numbers[i]}</span>
                          {memberOf(i) && <Avatar name={memberOf(i)!.name} url={memberOf(i)!.avatarUrl} size={18} />}
                          <span className="demo-player-name">{p.name}</span>
                        </button>
                        {v?.bomb && <span className="demo-tag is-bomb">C4</span>}
                        {v?.kit && side === "ct" && alive && <span className="demo-tag">{t("demos.viewer.kit")}</span>}
                        <span className="muted small demo-weapon">{alive ? weaponLabel(v?.weapon ?? "") : t("demos.viewer.dead")}</span>
                      </div>
                      <span
                        className="demo-hp"
                        role="img"
                        aria-label={t("demos.viewer.hp", {
                          hp: alive ? v!.hp : 0,
                        })}
                      >
                        <span style={{ width: `${alive ? v!.hp : 0}%` }} />
                      </span>
                    </th>
                    <td className="num mono">${v?.money ?? 0}</td>
                    <td className="num mono">
                      {r.kills}/{r.deaths}/{r.assists}
                    </td>
                    <td className="num mono">{r.adr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      })}
    </div>
  );
}

function RoundEvents({ replay, round, tick, onSeek, numbers }: { replay: Replay; round: RoundIndex; tick: number; onSeek: (s: number) => void; numbers: number[] }) {
  const { t } = useTranslation();
  const r = round.round;
  const at = (x: number) => (x - r.startTick) / replay.tickRate;
  const name = (i: number) => (i >= 0 ? replay.players[i]?.name : t("demos.viewer.world"));
  const sideOf = (i: number) => (i >= 0 && replay.players[i].team === r.tTeam ? "t" : "ct");
  const events = [
    ...round.kills.map((k) => ({
      tick: k.tick,
      key: `k${k.tick}${k.victim}`,
      node: <KillParts k={k} name={name} sideOf={sideOf} numbers={numbers} />,
    })),
    ...replay.bomb
      .filter((b) => b.tick >= r.startTick && b.tick <= r.postTick && b.type !== "plant_start" && b.type !== "defuse_start")
      .map((b) => ({
        tick: b.tick,
        key: `b${b.tick}`,
        node: <span className="demo-ev-bomb">{t(`demos.viewer.bomb.${b.type}`, { name: name(b.player) })}</span>,
      })),
    {
      tick: r.endTick,
      key: "end",
      node: (
        <b>
          {r.winner
            ? t("demos.viewer.won", {
                side: t(`live.sideShort.${r.winner}`),
                why: t(`demos.viewer.reason.${r.reason ?? "other"}`, {
                  defaultValue: "",
                }),
              })
            : t("demos.viewer.roundOver")}
        </b>
      ),
    },
  ].sort((a, b) => a.tick - b.tick);
  return (
    <ol className="demo-events">
      {events.map((e) => (
        <li key={e.key} className={e.tick <= tick ? "is-past" : ""}>
          <button type="button" onClick={() => onSeek(Math.max(0, at(e.tick) - 2))}>
            <span className="mono small muted">{clock(at(e.tick))}</span>
            {e.node}
          </button>
        </li>
      ))}
    </ol>
  );
}

function Transport({
  replay,
  round,
  play,
  notes,
  timeline,
  ours,
  children,
}: {
  replay: Replay;
  round: RoundIndex;
  play: ReturnType<typeof useReplayClock>;
  /** This round's notes the timeline shows. */
  notes: DemoComment[];
  timeline: TimelineMarks;
  /** Our team in the replay (0 or 1), for "ours". */
  ours: 0 | 1;
  /** More controls at the end of the bar (the settings). */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const r = round.round;
  const len = play.length || 1;
  const share = (tick: number) => (tick - r.startTick) / replay.tickRate / len;
  const pos = (tick: number) => `${(share(tick) * 100).toFixed(2)}%`;
  const ourPlayer = (i: number) => i >= 0 && replay.players[i]?.team === ours;
  const kills = timeline.kills === "none" ? [] : round.kills.filter((k) => timeline.kills === "all" || ourPlayer(k.killer));
  const throws = timeline.utility === "none" ? [] : round.grenades.filter((g) => timeline.utility === "all" || ourPlayer(g.thrower));
  // Notes that last are bars; one starting under another goes on a second lane.
  const lanes = new Map<string, number>();
  const laneEnd = [-Infinity, -Infinity];
  for (const n of [...notes].filter((x) => x.endTick !== null).sort((a, b) => a.tick - b.tick)) {
    const lane = n.tick >= laneEnd[0] ? 0 : 1;
    laneEnd[lane] = Math.max(laneEnd[lane], n.endTick!);
    lanes.set(n.id, lane);
  }
  // The note under the pointer, its text shown above the timeline.
  const [hover, setHover] = useState<{ note: DemoComment; at: number } | null>(null);
  const findNote = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!notes.length) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - box.left) / box.width;
    const near = 8 / Math.max(1, box.width);
    const hit = notes.find((n) => (n.endTick !== null ? x >= share(n.tick) - near && x <= share(n.endTick) + near : Math.abs(x - share(n.tick)) <= near));
    setHover(hit ? { note: hit, at: Math.min(0.9, Math.max(0.1, x)) } : null);
  };
  return (
    <div className="card demo-transport">
      <div className="demo-buttons">
        <button
          type="button"
          className="btn btn-quiet btn-small"
          aria-label={t("demos.viewer.prevRound")}
          title={`${t("demos.viewer.prevRound")} ([)`}
          disabled={play.roundIndex === 0}
          onClick={() => play.goRound(play.roundIndex - 1)}
        >
          <Icon name="prev" />
        </button>
        <button
          type="button"
          className="btn btn-primary demo-play"
          aria-label={play.playing ? t("player.pause") : t("player.play")}
          title={`${play.playing ? t("player.pause") : t("player.play")} (Space)`}
          onClick={play.toggle}
        >
          <Icon name={play.playing ? "pause" : "play"} />
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-small"
          aria-label={t("demos.viewer.nextRound")}
          title={`${t("demos.viewer.nextRound")} (])`}
          disabled={play.roundIndex >= replay.rounds.length - 1}
          onClick={() => play.goRound(play.roundIndex + 1)}
        >
          <Icon name="next" />
        </button>
        <span className="mono small demo-time">
          {clock(play.time)} / {clock(play.length)}
        </span>
      </div>
      <div className="demo-timeline" onPointerMove={findNote} onPointerLeave={() => setHover(null)}>
        <div className="demo-freeze" style={{ width: pos(r.liveTick) }} aria-hidden="true" />
        {throws.map((g, i) => (
          <span key={`g${i}`} className={`demo-mark is-util is-${g.kind}`} style={{ left: pos(g.throwTick) }} aria-hidden="true" />
        ))}
        {kills.map((k) => (
          <span
            key={`${k.tick}-${k.victim}`}
            className={`demo-mark is-kill ${k.victim >= 0 && replay.players[k.victim].team === r.tTeam ? "side-ct" : "side-t"}`}
            style={{ left: pos(k.tick) }}
            aria-hidden="true"
          />
        ))}
        {round.plantTick !== null && timeline.bomb === "show" && <span className="demo-mark is-plant" style={{ left: pos(round.plantTick) }} aria-hidden="true" />}
        <span className="demo-mark is-end" style={{ left: pos(r.endTick) }} aria-hidden="true" />
        {notes.map((n) =>
          n.endTick !== null ? (
            <span
              key={n.id}
              className={`demo-span${lanes.get(n.id) ? " is-lane2" : ""}`}
              style={{ left: pos(n.tick), width: `${(Math.max(0, share(n.endTick) - share(n.tick)) * 100).toFixed(2)}%` }}
              aria-hidden="true"
            />
          ) : (
            <span key={n.id} className="demo-mark is-note" style={{ left: pos(n.tick) }} aria-hidden="true" />
          ),
        )}
        {hover && (
          <div className="demo-tip" style={{ left: `${(hover.at * 100).toFixed(1)}%` }} role="status">
            <span className="mono small">
              {[
                t("demos.notes.at", { n: r.number, clock: noteWhen(hover.note) || clock((hover.note.tick - r.startTick) / replay.tickRate) }),
                hover.note.players.length ? t("demos.notes.players", { count: hover.note.players.length }) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
            <span className="demo-tip-text">{hover.note.text}</span>
          </div>
        )}
        <input
          type="range"
          className="demo-seek"
          min={0}
          max={len}
          step={1 / replay.sampleRate}
          value={play.time}
          aria-label={t("demos.viewer.position")}
          aria-valuetext={clock(play.time)}
          onChange={(e) => play.seek(Number(e.target.value))}
        />
      </div>
      <div className="demo-transport-end">
        <SpeedMenu speeds={REPLAY_SPEEDS} speed={play.speed} onChange={(s) => play.setSpeed(s as Speed)} />
        {children}
      </div>
    </div>
  );
}

function RoundStrip({ replay, current, onPick }: { replay: Replay; current: number; onPick: (i: number) => void }) {
  const { t } = useTranslation();
  return (
    <nav className="demo-rounds" aria-label={t("demos.viewer.rounds")}>
      {replay.rounds.map((r, i) => {
        const swap = i > 0 && replay.rounds[i - 1].tTeam !== r.tTeam;
        return (
          <span key={r.number} className="demo-round-wrap">
            {swap && <span className="demo-half" aria-hidden="true" />}
            <button
              type="button"
              className={`demo-round${r.winner ? ` won-${r.winner}` : ""}`}
              aria-current={i === current ? "true" : undefined}
              aria-label={t("demos.viewer.roundPick", {
                n: r.number,
                side: r.winner ? t(`live.sideShort.${r.winner}`) : "–",
              })}
              onClick={() => onPick(i)}
            >
              {r.number}
            </button>
          </span>
        );
      })}
    </nav>
  );
}
